import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Calendar, Star } from "lucide-react";
import { api, resolveImg } from "@/lib/api";

// Curated franchise history — Oilers era → Tennessee era.
// Source: tennesseetitans.com/history/historical-highlights
const ERAS = [
  {
    id: "founding",
    name: "Founding Era",
    years: "1959 – 1965",
    color: "from-amber-600 to-amber-900",
    image: "https://images.unsplash.com/photo-1577471488278-16eec37ffcc2?w=1200&q=80",
    highlights: [
      { year: 1959, title: "An AFL franchise is born", body: 'On August 3, K.S. "Bud" Adams, Jr. announces Houston\'s entry into the new American Football League. On October 31 he names the team the "Oilers" — for sentimental and social reasons.' },
      { year: 1960, title: "First game, first win", body: "Oilers sign LSU Heisman winner Billy Cannon, then beat Oakland 37-22 in the franchise's first regular-season game. Lou Rymkus named AFL's first Coach of the Year." },
      { year: 1961, title: "Back-to-back AFL Champions", body: "Oilers defeat the LA Chargers 24-16 for the first AFL Championship, then repeat the next year. Blanda throws 36 TDs; team becomes first pro team to score 500+ points in a season." },
      { year: 1962, title: "Three-peat division title", body: "Oilers win a third consecutive AFL Eastern Division crown at 11-3, but lose the title game to Dallas in the historic six-quarter classic." },
    ],
  },
  {
    id: "astrodome",
    name: "Astrodome Era",
    years: "1968 – 1974",
    color: "from-titans-blue to-blue-900",
    image: "https://images.unsplash.com/photo-1495555687398-3f50d6e79e1e?w=1200&q=80",
    highlights: [
      { year: 1968, title: "First domed home in pro football", body: "Oilers play their first game in the Astrodome — the first pro football team to play home games indoors." },
      { year: 1971, title: "Ken Houston's record day", body: "S Ken Houston sets the all-time pro record by returning two interceptions for TDs in one game (vs. Chargers); finishes the year with four pick-sixes — also an NFL record." },
    ],
  },
  {
    id: "luvyablue",
    name: "Luv Ya Blue",
    years: "1975 – 1980",
    color: "from-titans-red to-red-900",
    image: "https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=1200&q=80",
    highlights: [
      { year: 1975, title: "Bum Phillips takes over", body: "Bum Phillips named head coach. Oilers go 10-4 in his first season — their best record since 1962." },
      { year: 1978, title: "Earl Campbell arrives", body: "Oilers trade up to draft Texas's Heisman winner Earl Campbell. Campbell wins Rookie of the Year, leads the team to the AFC Championship Game." },
      { year: 1979, title: "Tyler Rose dominates", body: "Campbell wins his second straight rushing crown; Oilers return to the AFC title game (lose again to Steelers). Vernon Perry sets an NFL playoff record with 4 INTs vs. San Diego." },
      { year: 1980, title: "Campbell's record season", body: "Earl Campbell rushes for 1,934 yards — second-highest single-season total in NFL history at the time. Four 200-yard rushing games, another NFL record." },
    ],
  },
  {
    id: "runandshoot",
    name: "Run-and-Shoot",
    years: "1987 – 1993",
    color: "from-emerald-600 to-emerald-900",
    image: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1200&q=80",
    highlights: [
      { year: 1987, title: "Glanville and the House of Pain", body: "Oilers reach the playoffs for the first time since 1980. Warren Moon throws 21 TDs (most since Blanda in 1963)." },
      { year: 1990, title: "Moon shatters records", body: "Warren Moon throws for 527 yards in a single game (2nd-best in NFL history at the time) and becomes the all-time Oilers passing leader, surpassing George Blanda." },
      { year: 1991, title: "First AFC Central title", body: "Oilers win their first-ever undisputed AFC Central championship by beating Pittsburgh 31-6." },
      { year: 1993, title: "11-game win streak", body: "Houston wins 11 in a row to close the regular season — still one of the longest streaks in franchise history." },
    ],
  },
  {
    id: "transition",
    name: "The Move",
    years: "1996 – 1998",
    color: "from-purple-700 to-indigo-900",
    image: "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1200&q=80",
    highlights: [
      { year: 1996, title: "NFL approves the relocation", body: "In April, the NFL approves the franchise's relocation from Houston to Nashville. The Oilers play two final seasons in Memphis and Vanderbilt Stadium as the transition completes." },
      { year: 1998, title: "Final Oilers season", body: "Last season under the Oilers name; the team plays at Vanderbilt Stadium awaiting their new Nashville home." },
    ],
  },
  {
    id: "titans",
    name: "Titans Era",
    years: "1999 – 2003",
    color: "from-titans-blue via-titans-red to-titans-blue",
    image: "https://images.unsplash.com/photo-1577223625816-7546f13df25d?w=1200&q=80",
    highlights: [
      { year: 1999, title: "Music City Miracle & Super Bowl run", body: "The rebranded Tennessee Titans go 13-3, beat Buffalo on the Music City Miracle, and reach Super Bowl XXXIV — falling one yard short to the Rams. Eddie George and Steve McNair lead the way." },
      { year: 2000, title: "13-3 again", body: "Titans match their franchise-best 13-3 record, win the AFC Central, but fall in the divisional round." },
      { year: 2002, title: "Co-MVP McNair", body: "Steve McNair shares the NFL MVP award with Peyton Manning; Titans reach the AFC Championship Game." },
    ],
  },
  {
    id: "fishercj",
    name: "Fisher & CJ2K",
    years: "2008 – 2010",
    color: "from-sky-700 to-blue-900",
    image: "https://images.unsplash.com/photo-1551958219-acbc608c6377?w=1200&q=80",
    highlights: [
      { year: 2008, title: "13-3, AFC's #1 seed", body: "Titans go 13-3 and earn the AFC's top seed. Six Pro Bowlers — Mawae, Roos, Chris Johnson, Haynesworth, Hope, Finnegan. Ring of Honor unveiled at LP Field." },
      { year: 2009, title: "Chris Johnson hits 2,000", body: "Chris Johnson rushes for 2,006 yards — joining the 2,000-yard club. He also sets the NFL record for total yards from scrimmage in a season (2,509)." },
    ],
  },
  {
    id: "exoticsmashmouth",
    name: "Exotic Smashmouth",
    years: "2017 – 2021",
    color: "from-titans-red to-rose-900",
    image: "https://images.unsplash.com/photo-1546608235-3310a2494cdf?w=1200&q=80",
    highlights: [
      { year: 2018, title: "First playoff win in 14 years", body: "Titans erase a 21-3 halftime deficit to beat the Chiefs in Kansas City — the second-largest playoff road comeback in NFL history." },
      { year: 2020, title: "AFC Championship run", body: "Derrick Henry runs over the Patriots and Ravens in back-to-back postseason wins (franchise-record 182 and 195 yards). Titans reach the AFC Championship Game for the first time since 2002." },
      { year: 2021, title: "Henry's 2,000-yard season", body: "Derrick Henry rushes for 2,027 yards and wins his second straight NFL rushing title. McNair (#9) and Eddie George (#27) jerseys retired." },
      { year: 2022, title: "Back-to-back AFC South", body: "Titans claim back-to-back AFC South crowns and earn the AFC's #1 seed — first time since 2008. Mike Vrabel wins AP NFL Coach of the Year." },
    ],
  },
  {
    id: "newera",
    name: "New Era",
    years: "2023 – Present",
    color: "from-indigo-700 to-titans-blue",
    image: "https://images.unsplash.com/photo-1521405924368-64c5b84bec60?w=1200&q=80",
    highlights: [
      { year: 2023, title: "New stadium approved", body: "Nashville Metro Council approves a new $2.1B enclosed stadium on the East Bank — a 30-year lease anchoring the Titans in Nashville through 2053." },
    ],
  },
];

const StatCard = ({ icon: Icon, value, label }) => (
  <div className="titans-card p-5 text-center">
    <Icon size={20} className="mx-auto text-titans-blue mb-2" />
    <div className="font-display text-3xl text-white">{value}</div>
    <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver mt-1">{label}</div>
  </div>
);

export default function History() {
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/heritage");
        setOverrides(data.eras || {});
      } catch (e) {
        // Non-fatal — fall back to default banners.
        console.error("Heritage overrides failed:", e);
      }
    })();
  }, []);

  return (
    <div data-testid="history-page" className="max-w-[1280px] mx-auto px-5 md:px-8 py-12">
      <div className="flex items-center gap-2 mb-3">
        <Trophy size={14} className="text-titans-red" />
        <span className="font-cond uppercase tracking-[0.3em] text-xs text-titans-red">Heritage</span>
      </div>
      <h1 className="font-display text-5xl md:text-7xl uppercase tracking-wide section-title">
        Titans History
      </h1>
      <p className="text-titans-silver font-body mt-4 max-w-3xl text-lg leading-relaxed">
        From Houston Oilers founder Bud Adams in 1959 through the Music City Miracle, Earl Campbell, CJ2K, Derrick Henry,
        and the new East Bank stadium — six decades of Two-Tone Blue football, year by year.
      </p>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
        <StatCard icon={Trophy} value="2" label="AFL Championships" />
        <StatCard icon={Star} value="1" label="Super Bowl Appearance" />
        <StatCard icon={Calendar} value="65+" label="Years of Football" />
        <StatCard icon={Trophy} value="9" label="Division Titles" />
      </div>

      {/* Eras */}
      <div className="mt-16 space-y-16">
        {ERAS.map((era) => {
          const customImg = overrides[era.id];
          const heroImg = customImg ? resolveImg(customImg) : null;
          return (
          <section key={era.id} data-testid={`era-${era.id}`} className="relative">
            <div className="relative overflow-hidden h-44 md:h-56 mb-6 bg-titans-navy ring-1 ring-titans-blue/40 shadow-[0_10px_30px_rgba(0,0,0,0.5)]">
              {heroImg ? (
                <>
                  <img
                    src={heroImg}
                    alt={era.name}
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid={`era-${era.id}-img`}
                  />
                  {/* Clean Titans-branded vignette — keep the photo as the hero, just darken the bottom for legible type. */}
                  <div className="absolute inset-0 bg-gradient-to-t from-titans-void via-titans-void/55 to-transparent" />
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-titans-blue/40 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-titans-blue via-titans-red to-titans-blue" />
                </>
              ) : (
                <>
                  {/* Branded default — no photo override yet. */}
                  <div className="absolute inset-0 bg-gradient-to-br from-titans-navy via-titans-navy to-titans-void" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-titans-blue/25 via-transparent to-titans-red/15" />
                  <img
                    src="/titan-standard-logo.png"
                    alt=""
                    aria-hidden
                    className="absolute -right-6 -bottom-6 h-56 md:h-64 w-auto opacity-15 select-none pointer-events-none"
                  />
                  <div
                    className="absolute inset-0 opacity-20 mix-blend-overlay"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(135deg, rgba(255,255,255,0.08) 0 2px, transparent 2px 16px)",
                    }}
                  />
                  <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-titans-blue via-titans-red to-titans-blue" />
                </>
              )}
              <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                <div className="font-cond uppercase tracking-[0.3em] text-[10px] text-titans-blue mb-1">
                  {era.years}
                </div>
                <h2 className="font-display text-3xl md:text-5xl uppercase tracking-wide text-white leading-none drop-shadow-[0_2px_12px_rgba(0,0,0,0.85)]">
                  {era.name}
                </h2>
              </div>
            </div>

            <div className="space-y-4">
              {era.highlights.map((h) => (
                <div key={`${era.id}-${h.year}`} className="titans-card p-5 md:p-6 border-l-4 border-titans-red flex flex-col md:flex-row gap-4">
                  <div className="md:w-32 shrink-0">
                    <div className="font-display text-4xl md:text-5xl text-titans-blue leading-none">{h.year}</div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-xl md:text-2xl uppercase tracking-wide text-white">{h.title}</h3>
                    <p className="text-titans-silver font-body mt-2 leading-relaxed">{h.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
          );
        })}
      </div>

      <div className="mt-20 titans-card p-6 md:p-8 border-l-4 border-titans-blue text-center" data-testid="history-credit">
        <div className="font-cond uppercase tracking-[0.3em] text-[10px] text-titans-blue mb-2">Source &amp; Credits</div>
        <p className="text-titans-silver font-body text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
          Historical highlights, dates, and franchise milestones on this page are sourced from the official Tennessee Titans historical archive:
        </p>
        <a
          href="https://www.tennesseetitans.com/history/historical-highlights"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="history-credit-link"
          className="inline-block mt-3 text-titans-blue hover:text-white font-cond uppercase tracking-wider text-sm border-b border-titans-blue hover:border-white transition-colors break-all"
        >
          tennesseetitans.com/history/historical-highlights
        </a>
        <p className="text-titans-muted font-body text-xs italic mt-4">
          The Titan Standard is an independent fan publication and is not affiliated with or endorsed by the Tennessee Titans or the NFL.
        </p>
        <Link to="/" className="inline-block mt-5 text-titans-silver hover:text-white font-cond uppercase tracking-wider text-xs">
          ← Back to Home
        </Link>
      </div>
    </div>
  );
}
